import React, { useState } from "react";
import PropTypes from "prop-types";
import { Link, useHistory, useLocation } from "react-router-dom";
import {
  AppBar,
  Toolbar,
  Box,
  IconButton,
  Badge,
  Avatar,
  Typography,
  MenuItem,
  Divider,
  Stack,
  Tooltip,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import {
  NotificationsOutlined as NotificationIcon,
  Menu as MenuIcon,
  KeyboardArrowDown as ArrowDownIcon,
  Logout as LogoutIcon,
  PersonOutline as ProfileIcon,
  ContentCopy as CopyIcon,
} from "@mui/icons-material";
import { colors, borderRadiusPx } from "../../../../theme";
import { Path } from "../../../../static/Constants";
import { CommonMenu } from "../../ui";

/**
 * DashboardTopNav - Top app header with notifications, wallet address, and creator profile dropdown.
 */
const DashboardTopNav = ({
  role,
  onMobileToggle,
  onToggleSidebar,
  sidebarOpen = true,
  creatorAddress = "",
  userName = "",
  userRole = "",
  unreadCount = 0,
}) => {
  const history = useHistory();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [anchorEl, setAnchorEl] = useState(null);
  const [copied, setCopied] = useState(false);
  const open = Boolean(anchorEl);
  const tn = colors.topNav;

  const isAdminRole =
    role === "admin" ||
    userRole?.toLowerCase().includes("admin") ||
    location.pathname.startsWith("/admin") ||
    location.pathname === Path.admin;

  const notificationsPath = isAdminRole ? Path.adminNotifications : Path.orgNotifications;
  const profilePath = Path.profile;

  const handleProfileClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const shortAddress = creatorAddress
    ? `${creatorAddress.slice(0, 6)}…${creatorAddress.slice(-4)}`
    : "";

  const displayName = userName || (creatorAddress ? `Creator (${shortAddress})` : "Creator");

  const handleCopyAddress = () => {
    if (creatorAddress) {
      navigator.clipboard.writeText(creatorAddress);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleToggle = (e) => {
    if (isMobile) {
      if (onMobileToggle) onMobileToggle(e);
    } else {
      if (onToggleSidebar) onToggleSidebar(e);
      else if (onMobileToggle) onMobileToggle(e);
    }
  };

  return (
    <AppBar
      position="sticky"
      elevation={0}
      sx={{
        backgroundColor: tn.bg,
        borderBottom: `1px solid ${tn.border}`,
        zIndex: 1100,
      }}
    >
      <Toolbar
        sx={{
          minHeight: "64px !important",
          px: { xs: 2, md: 3 },
          display: "flex",
          justifyContent: "space-between",
        }}
      >
        {/* Left Side: Menu Toggle Button */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {(isMobile || !sidebarOpen) && (
            <IconButton
              color="inherit"
              edge="start"
              onClick={handleToggle}
              sx={{
                color: tn.text,
                p: 1,
                borderRadius: borderRadiusPx.lg,
                "&:hover": {
                  backgroundColor: tn.iconBtnBg,
                },
              }}
            >
              <MenuIcon />
            </IconButton>
          )}
        </Box>

        {/* Right Side: Notifications & User Profile */}
        <Stack direction="row" spacing={2} alignItems="center">
          {/* Notifications Button */}
          <Tooltip title={isAdminRole ? "View Notifications" : "View Notifications"}>
            <IconButton
              component={Link}
              to={notificationsPath}
              sx={{
                color: tn.subtext,
                backgroundColor: tn.iconBtnBg,
                p: 1,
                borderRadius: borderRadiusPx.lg,
                "&:hover": {
                  backgroundColor: tn.iconBtnHover,
                  color: tn.text,
                },
              }}
            >
              <Badge
                badgeContent={unreadCount}
                sx={{
                  "& .MuiBadge-badge": {
                    backgroundColor: tn.badge,
                    color: colors.neutral.white,
                    fontSize: "0.68rem",
                    fontWeight: 700,
                    height: 18,
                    minWidth: 18,
                  },
                }}
              >
                <NotificationIcon sx={{ fontSize: "1.25rem" }} />
              </Badge>
            </IconButton>
          </Tooltip>

          {/* User Profile / Wallet Chip */}
          <Box
            onClick={handleProfileClick}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.25,
              cursor: "pointer",
              p: "4px 8px 4px 6px",
              borderRadius: borderRadiusPx.xl,
              transition: "background 0.2s",
              "&:hover": {
                backgroundColor: tn.iconBtnBg,
              },
            }}
          >
            <Avatar
              sx={{
                width: 36,
                height: 36,
                backgroundColor: tn.avatarBg,
                fontWeight: 700,
                fontSize: "0.85rem",
              }}
            >
              {displayName.charAt(0)}
            </Avatar>

            <Box sx={{ display: { xs: "none", sm: "block" }, textAlign: "left" }}>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 600,
                  fontSize: "0.83rem",
                  color: tn.text,
                  lineHeight: 1.2,
                }}
              >
                {displayName}
              </Typography>
              {userRole && (
                <Typography
                  variant="caption"
                  sx={{
                    color: tn.subtext,
                    fontSize: "0.72rem",
                    lineHeight: 1.1,
                    display: "block",
                  }}
                >
                  {shortAddress ? `${userRole} (${shortAddress})` : userRole}
                </Typography>
              )}
            </Box>

            <ArrowDownIcon sx={{ color: tn.subtext, fontSize: "1.1rem" }} />
          </Box>

          {/* Dropdown Menu */}
          <CommonMenu
            anchorEl={anchorEl}
            open={open}
            onClose={handleClose}
            width={220}
            shadow="dropdownMenu"
          >
            <Box sx={{ px: 2, py: 1.5 }}>
              <Typography variant="body2" sx={{ fontWeight: 700, color: colors.slate[900] }}>
                {displayName}
              </Typography>
              {userRole && (
                <Typography variant="caption" sx={{ color: colors.slate[500] }}>
                  {userRole}
                </Typography>
              )}
              {creatorAddress && (
                <Stack
                  direction="row"
                  spacing={0.5}
                  alignItems="center"
                  sx={{
                    mt: 1,
                    p: "4px 8px",
                    borderRadius: borderRadiusPx.sm,
                    backgroundColor: colors.slate[50],
                    border: `1px solid ${colors.dashboard.cardBorder}`,
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      fontFamily: "monospace",
                      fontSize: "0.75rem",
                      color: colors.slate[700],
                      flex: 1,
                    }}
                  >
                    {shortAddress}
                  </Typography>
                  <Tooltip title={copied ? "Copied!" : "Copy Address"}>
                    <IconButton size="small" onClick={handleCopyAddress} sx={{ p: 0.2 }}>
                      <CopyIcon sx={{ fontSize: "0.8rem", color: colors.slate[500] }} />
                    </IconButton>
                  </Tooltip>
                </Stack>
              )}
            </Box>

            <Divider />

            <MenuItem
              component={Link}
              to={profilePath}
              onClick={handleClose}
              sx={{ fontSize: "0.84rem", py: 1 }}
            >
              <ProfileIcon sx={{ fontSize: "1.1rem", mr: 1.5, color: colors.slate[500] }} />
              Profile & Settings
            </MenuItem>

            <Divider />

            <MenuItem
              onClick={() => {
                handleClose();
                history.push(Path.root);
              }}
              sx={{ fontSize: "0.84rem", py: 1, color: colors.error.main }}
            >
              <LogoutIcon sx={{ fontSize: "1.1rem", mr: 1.5, color: colors.error.main }} />
              {isAdminRole ? "Exit Admin" : "Exit Creator"}
            </MenuItem>
          </CommonMenu>
        </Stack>
      </Toolbar>
    </AppBar>
  );
};

DashboardTopNav.propTypes = {
  role: PropTypes.string,
  onMobileToggle: PropTypes.func,
  onToggleSidebar: PropTypes.func,
  sidebarOpen: PropTypes.bool,
  creatorAddress: PropTypes.string,
  userName: PropTypes.string,
  userRole: PropTypes.string,
  unreadCount: PropTypes.number,
};

export default DashboardTopNav;
